/**
 * audioS3Manager.js
 * 
 * Manages audio recording to S3 with the following responsibilities:
 * 1. Fetch presigned URLs from backend
 * 2. Record audio in 5-second chunks via ExtendableMediaRecorder with WAV encoder
 * 3. Store chunks in IndexedDB
 * 4. Upload chunks to S3 using presigned URLs
 * 5. Manage URL queue with low-water-mark refill
 * 6. Handle retries and failures
 * 
 * IMPORTANT: Each chunk is encoded as a complete WAV file with headers,
 * ensuring independent playability and reliable backend merging.
 */

import configDetails from '../Config/Config';
import logger from './Logger';
import { Auth } from 'aws-amplify';
import { getExtendableMediaRecorder, isWavEncoderReady } from './wavEncoderSetup';

// ==================== HELPER FUNCTIONS ====================

/**
 * Helper function to get authIdToken from AWS Amplify
 */
async function getAuthIdToken() {
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    const token = currentUser.signInUserSession.idToken.jwtToken;
    return token;
  } catch (error) {
    logger.error('Error getting auth token', {
      component: 'audioS3Manager',
      action: 'GET_AUTH_TOKEN_FAILED',
      error
    });
    return null;
  }
}

// ==================== CONSTANTS ====================
const DB_NAME = 'AudioRecordingDB';
const DB_VERSION = 1;
const STORE_NAME = 'audioChunks';
const CHUNK_DURATION_MS = configDetails.chunkDurationMs; // 5 seconds (from config)
const URL_REFILL_THRESHOLD = configDetails.urlRefillThreshold; // Refill when queue has 3 or fewer URLs (from config)
const URL_BATCH_SIZE = configDetails.urlBatchSize; // Request 10 URLs at a time (from config)
const MAX_RETRY_COUNT = configDetails.maxRetryCount; // 3 retries (from config)
const UPLOAD_RETRY_DELAY_MS = configDetails.uploadRetryDelayMs; // 2 seconds between retries (from config)

// ==================== STATE VARIABLES ====================
let presignedQueue = []; // FIFO queue for presigned URLs
let db = null; // IndexedDB instance
let mediaRecorder = null;
let chunkSequence = 0; // Auto-increment sequence per conversation
let isRecordingActive = false;
let isUploadWorkerActive = false;
let currentConversationId = null;
let currentUserId = null;
let uploadWorkerPromise = null;
let isPausedState = false;
let chunkTimerRef = null; // Timer to force stop/start per chunk for WAV finalization

// ==================== INDEXEDDB OPERATIONS ====================

/**
 * Initialize IndexedDB database
 */
async function initializeDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      logger.error('Failed to open IndexedDB', {
        component: 'audioS3Manager',
        action: 'DB_INIT_FAILED',
        error: request.error
      });
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      logger.info('IndexedDB initialized successfully', {
        component: 'audioS3Manager',
        action: 'DB_INIT_SUCCESS'
      });
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Create object store if it doesn't exist
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = database.createObjectStore(STORE_NAME, {
          keyPath: ['conversation_id', 'chunk_seq']
        });

        // Create indexes for efficient querying
        objectStore.createIndex('conversation_status', ['conversation_id', 'status'], { unique: false });
        objectStore.createIndex('conversation_seq', ['conversation_id', 'chunk_seq'], { unique: true });

        logger.info('IndexedDB object store created', {
          component: 'audioS3Manager',
          action: 'DB_STORE_CREATED',
          context: { storeName: STORE_NAME }
        });
      }
    };
  });
}

/**
 * Add audio chunk to IndexedDB
 */
async function addChunkToDb(conversationId, blob) {
  if (!db) {
    throw new Error('Database not initialized');
  }

  chunkSequence++;

  const chunkRecord = {
    conversation_id: conversationId,
    chunk_seq: chunkSequence,
    blob: blob,
    status: 'pending',
    retry_count: 0,
    created_at: Date.now()
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.add(chunkRecord);

    request.onsuccess = () => {
      logger.debug('Audio chunk added to IndexedDB', {
        component: 'audioS3Manager',
        action: 'CHUNK_ADDED',
        context: {
          conversationId,
          chunkSeq: chunkSequence,
          blobSize: blob.size
        }
      });
      resolve(chunkRecord);
    };

    request.onerror = () => {
      logger.error('Failed to add chunk to IndexedDB', {
        component: 'audioS3Manager',
        action: 'CHUNK_ADD_FAILED',
        error: request.error
      });
      reject(request.error);
    };
  });
}

/**
 * Get oldest pending chunk from IndexedDB
 */
async function getOldestPendingChunk(conversationId) {
  if (!db) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const index = objectStore.index('conversation_status');
    const range = IDBKeyRange.only([conversationId, 'pending']);
    const request = index.openCursor(range);

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        resolve(cursor.value);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      logger.error('Failed to get pending chunk', {
        component: 'audioS3Manager',
        action: 'GET_PENDING_FAILED',
        error: request.error
      });
      reject(request.error);
    };
  });
}

/**
 * Update chunk status in IndexedDB
 */
async function updateChunkStatus(conversationId, chunkSeq, status, retryCount = 0) {
  if (!db) {
    return;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.get([conversationId, chunkSeq]);

    request.onsuccess = () => {
      const chunk = request.result;
      if (chunk) {
        chunk.status = status;
        chunk.retry_count = retryCount;

        const updateRequest = objectStore.put(chunk);
        updateRequest.onsuccess = () => resolve(chunk);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete chunk from IndexedDB
 */
async function deleteChunk(conversationId, chunkSeq) {
  if (!db) {
    return;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.delete([conversationId, chunkSeq]);

    request.onsuccess = () => {
      logger.debug('Chunk deleted from IndexedDB', {
        component: 'audioS3Manager',
        action: 'CHUNK_DELETED',
        context: { conversationId, chunkSeq }
      });
      resolve();
    };

    request.onerror = () => {
      logger.error('Failed to delete chunk', {
        component: 'audioS3Manager',
        action: 'CHUNK_DELETE_FAILED',
        error: request.error
      });
      reject(request.error);
    };
  });
}

/**
 * Get count of pending chunks
 */
async function getPendingChunksCount(conversationId) {
  if (!db) {
    return 0;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const index = objectStore.index('conversation_status');
    const range = IDBKeyRange.only([conversationId, 'pending']);
    const request = index.count(range);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get failed chunks and requeue them as pending
 */
async function requeueFailedChunks(conversationId) {
  if (!db) {
    return 0;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const index = objectStore.index('conversation_status');
    const range = IDBKeyRange.only([conversationId, 'failed']);
    const request = index.openCursor(range);

    let requeuedCount = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const chunk = cursor.value;

        // Only requeue if retry count is less than max
        if (chunk.retry_count < MAX_RETRY_COUNT) {
          chunk.status = 'pending';
          cursor.update(chunk);
          requeuedCount++;
        }

        cursor.continue();
      } else {
        logger.info('Failed chunks requeued', {
          component: 'audioS3Manager',
          action: 'CHUNKS_REQUEUED',
          context: { conversationId, count: requeuedCount }
        });
        resolve(requeuedCount);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

// ==================== PRESIGNED URL MANAGEMENT ====================

/**
 * Fetch presigned URLs from backend
 */
async function fetchPresignedUrls(conversationId, userId, count = URL_BATCH_SIZE) {
  try {
    const { trackingUrl, AUDIO_PRESIGNED } = configDetails;
    const endpoint = `${trackingUrl}/${AUDIO_PRESIGNED}`;

    const payload = {
      conversation_id: conversationId,
      user_id: userId,
      count: count
    };

    logger.info('Fetching presigned URLs', {
      component: 'audioS3Manager',
      action: 'FETCH_PRESIGNED_URLS',
      context: { conversationId, userId, count }
    });

    // Get authorization token
    const token = await getAuthIdToken();
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = token;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch presigned URLs: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.items || !Array.isArray(data.items)) {
      throw new Error('Invalid response format from presigned URL endpoint');
    }

    // Add URLs to queue (FIFO)
    presignedQueue.push(...data.items);

    logger.info('Presigned URLs fetched successfully', {
      component: 'audioS3Manager',
      action: 'PRESIGNED_URLS_FETCHED',
      context: {
        conversationId,
        receivedCount: data.items.length,
        queueLength: presignedQueue.length
      }
    });

    return data.items;
  } catch (error) {
    logger.error('Failed to fetch presigned URLs', {
      component: 'audioS3Manager',
      action: 'FETCH_PRESIGNED_URLS_FAILED',
      error
    });
    throw error;
  }
}

/**
 * Get next URL from queue
 */
function getNextUrl() {
  if (presignedQueue.length === 0) {
    logger.warn('Presigned URL queue is empty', {
      component: 'audioS3Manager',
      action: 'URL_QUEUE_EMPTY'
    });
    return null;
  }

  const url = presignedQueue.shift(); // FIFO - remove from front

  logger.debug('URL retrieved from queue', {
    component: 'audioS3Manager',
    action: 'URL_RETRIEVED',
    context: {
      remainingUrls: presignedQueue.length,
      s3Key: url.s3_key
    }
  });

  return url;
}

/**
 * Check queue level and refill if needed (low-water-mark)
 */
async function checkAndRefillUrlQueue(conversationId, userId) {
  if (presignedQueue.length <= URL_REFILL_THRESHOLD) {
    logger.info('URL queue below threshold, refilling', {
      component: 'audioS3Manager',
      action: 'URL_QUEUE_REFILL',
      context: {
        currentQueueLength: presignedQueue.length,
        threshold: URL_REFILL_THRESHOLD
      }
    });

    try {
      await fetchPresignedUrls(conversationId, userId, URL_BATCH_SIZE);
    } catch (error) {
      logger.error('Failed to refill URL queue', {
        component: 'audioS3Manager',
        action: 'URL_QUEUE_REFILL_FAILED',
        error
      });
    }
  }
}

// ==================== MEDIARECORDER MANAGEMENT ====================

/**
 * Initialize MediaRecorder for S3 upload with WAV encoding
 * 
 * WAV encoding ensures:
 * - Each chunk is a complete, independently playable audio file
 * - Backend merging is trivial (concatenate PCM data)
 * - No silent chunks or header reconstruction needed
 */
function initializeMediaRecorder(stream, conversationId) {
  try {
    // Verify WAV encoder is initialized
    if (!isWavEncoderReady()) {
      logger.warn('WAV encoder not ready - ensure initializeWavEncoder() was called in app startup', {
        component: 'audioS3Manager',
        action: 'MEDIARECORDER_ENCODER_NOT_READY',
        context: { conversationId }
      });
    }

    const MediaRecorderClass = getExtendableMediaRecorder();
    
    const options = {
      mimeType: 'audio/wav', // WAV format ensures each chunk has full headers
      audioBitsPerSecond: 128000
    };

    // Check if the mimeType is supported
    if (!MediaRecorderClass.isTypeSupported(options.mimeType)) {
      logger.warn('WAV mimeType not supported, falling back to default', {
        component: 'audioS3Manager',
        action: 'MEDIARECORDER_MIMETYPE_FALLBACK',
        context: { preferredType: options.mimeType, conversationId }
      });
      mediaRecorder = new MediaRecorderClass(stream);
    } else {
      mediaRecorder = new MediaRecorderClass(stream, options);
    }

    // Handle data available event
    // WAV chunks are complete files with full headers
    mediaRecorder.ondataavailable = async (event) => {
      if (isPausedState) {
        logger.debug('Recording paused: ignoring audio chunk', {
          component: 'audioS3Manager',
          action: 'CHUNK_IGNORED_PAUSED',
          context: { conversationId }
        });
        return;
      }

      // FIX 4: Validate chunk size (44+ bytes = valid WAV header + data)
      if (event.data && event.data.size > 44) {
        try {
          await addChunkToDb(conversationId, event.data);

          logger.debug('Audio chunk captured (WAV encoded, independently playable)', {
            component: 'audioS3Manager',
            action: 'CHUNK_CAPTURED',
            context: {
              conversationId,
              chunkSeq: chunkSequence,
              size: event.data.size,
              format: 'audio/wav',
              has_wav_header: true,
              independently_playable: true
            }
          });
        } catch (error) {
          logger.error('Failed to save audio chunk', {
            component: 'audioS3Manager',
            action: 'CHUNK_SAVE_FAILED',
            error,
            context: { conversationId }
          });
        }
      } else if (event.data && event.data.size > 0) {
        logger.debug('Audio chunk too small - skipped', {
          component: 'audioS3Manager',
          action: 'CHUNK_SKIPPED_TOO_SMALL',
          context: {
            conversationId,
            size: event.data.size,
            minSize: 44
          }
        });
      }
    };

    mediaRecorder.onerror = (error) => {
      logger.error('MediaRecorder error occurred', {
        component: 'audioS3Manager',
        action: 'MEDIARECORDER_ERROR',
        error: error.error ? error.error.toString() : error.toString(),
        errorName: error.name,
        errorMessage: error.message,
        context: { conversationId }
      });
      
      // Set recording as inactive if there's an error
      isRecordingActive = false;
      
      // Clear timer on error
      if (chunkTimerRef) {
        clearInterval(chunkTimerRef);
        chunkTimerRef = null;
      }
    };

    logger.info('MediaRecorder initialized for S3 upload (WAV encoder)', {
      component: 'audioS3Manager',
      action: 'MEDIARECORDER_INITIALIZED',
      context: {
        mimeType: mediaRecorder.mimeType,
        conversationId,
        encoding: 'WAV',
        chunk_safety: 'Each chunk is independently playable'
      }
    });

    return mediaRecorder;
  } catch (error) {
    logger.error('Failed to initialize MediaRecorder', {
      component: 'audioS3Manager',
      action: 'MEDIARECORDER_INIT_FAILED',
      error,
      context: { conversationId }
    });
    throw error;
  }
}

/**
 * Start recording audio chunks
 */
/**
 * Start recording audio chunks
 * 
 * CRITICAL FOR WAV ENCODING:
 * We use a timer to force stop/start per chunk rather than timeslice.
 * This ensures each chunk is a complete WAV file with headers.
 */
function startRecording() {
  if (mediaRecorder && mediaRecorder.state === 'inactive') {
    isPausedState = false;
    try {
      // Start recording WITHOUT timeslice - we'll manually trigger chunks
      mediaRecorder.start();
      isRecordingActive = true;

      // Set up chunk timer to force WAV finalization per chunk
      // Every CHUNK_DURATION_MS, we stop (flushes WAV) and start (new WAV)
      if (chunkTimerRef) clearInterval(chunkTimerRef);
      chunkTimerRef = setInterval(() => {
        if (mediaRecorder && isRecordingActive && !isPausedState) {
          try {
            // Only proceed if recorder is in recording state
            if (mediaRecorder.state === 'recording') {
              // Stop forces the encoder to finalize the current WAV file
              mediaRecorder.stop();
              
              // Use a small delay before restarting to allow finalization
              setTimeout(() => {
                if (mediaRecorder && isRecordingActive && !isPausedState && mediaRecorder.state === 'inactive') {
                  try {
                    // Start immediately begins a new WAV file
                    mediaRecorder.start();
                  } catch (error) {
                    logger.error('Failed to restart MediaRecorder after chunk stop', {
                      component: 'audioS3Manager',
                      action: 'MEDIARECORDER_RESTART_FAILED',
                      error,
                      context: { conversationId: currentConversationId }
                    });
                  }
                }
              }, 50); // 50ms delay for finalization
            }
          } catch (error) {
            logger.error('Error in chunk timer interval', {
              component: 'audioS3Manager',
              action: 'CHUNK_TIMER_ERROR',
              error,
              context: { conversationId: currentConversationId }
            });
          }
        }
      }, CHUNK_DURATION_MS);

      logger.info('Audio recording started for S3 upload (WAV per-chunk finalization)', {
        component: 'audioS3Manager',
        action: 'RECORDING_STARTED',
        context: {
          chunkDuration: CHUNK_DURATION_MS,
          conversationId: currentConversationId,
          chunking_strategy: 'stop/start timer (ensures WAV finalization per chunk)'
        }
      });
    } catch (error) {
      logger.error('Failed to start MediaRecorder', {
        component: 'audioS3Manager',
        action: 'MEDIARECORDER_START_FAILED',
        error,
        context: { conversationId: currentConversationId }
      });
      isRecordingActive = false;
      throw error;
    }
  }
}

/**
 * Stop recording audio chunks
 */
function stopRecording() {
  // Clear the chunk timer (stops forcing stop/start cycles)
  if (chunkTimerRef) {
    clearInterval(chunkTimerRef);
    chunkTimerRef = null;
  }

  try {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      isRecordingActive = false;
      isPausedState = false;
      logger.info('Audio recording stopped', {
        component: 'audioS3Manager',
        action: 'RECORDING_STOPPED',
        context: {
          conversationId: currentConversationId,
          totalChunks: chunkSequence
        }
      });
    }
  } catch (error) {
    logger.error('Error stopping MediaRecorder', {
      component: 'audioS3Manager',
      action: 'MEDIARECORDER_STOP_FAILED',
      error,
      context: { conversationId: currentConversationId }
    });
    isRecordingActive = false;
  }
}

// ==================== UPLOAD WORKER ====================

/**
 * Upload a single chunk to S3
 */
async function uploadChunkToS3(chunk, urlItem) {
  try {
    logger.debug('Starting chunk upload', {
      component: 'audioS3Manager',
      action: 'UPLOAD_START',
      context: {
        conversationId: chunk.conversation_id,
        chunkSeq: chunk.chunk_seq,
        s3Key: urlItem.s3_key,
        blobSize: chunk.blob.size
      }
    });

    const response = await fetch(urlItem.upload_url, {
      method: 'PUT',
      body: chunk.blob,
      headers: {
        'Content-Type': 'audio/wav'
      }
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    logger.info('Chunk uploaded successfully', {
      component: 'audioS3Manager',
      action: 'UPLOAD_SUCCESS',
      context: {
        conversationId: chunk.conversation_id,
        chunkSeq: chunk.chunk_seq,
        s3Key: urlItem.s3_key
      }
    });

    return true;
  } catch (error) {
    logger.error('Chunk upload failed', {
      component: 'audioS3Manager',
      action: 'UPLOAD_FAILED',
      context: {
        conversationId: chunk.conversation_id,
        chunkSeq: chunk.chunk_seq,
        retryCount: chunk.retry_count
      },
      error
    });

    throw error;
  }
}

/**
 * Upload worker loop - runs continuously
 */
async function uploadWorkerLoop() {
  logger.info('Upload worker started', {
    component: 'audioS3Manager',
    action: 'UPLOAD_WORKER_START',
    context: { conversationId: currentConversationId }
  });

  while (isUploadWorkerActive || isRecordingActive) {
      if (!isUploadWorkerActive && !isRecordingActive) {
      logger.info('Upload worker exiting - flags cleared', {
        component: 'audioS3Manager',
        action: 'WORKER_CLEAN_EXIT'
      });
      break;
    }
    try {
      // Get oldest pending chunk
      const chunk = await getOldestPendingChunk(currentConversationId);

      if (!chunk) {
        // No pending chunks, wait a bit before checking again
        await new Promise(resolve => setTimeout(resolve, 1000));

        // If recording stopped and no pending chunks, exit
        if (!isRecordingActive) {
          const pendingCount = await getPendingChunksCount(currentConversationId);
          if (pendingCount === 0) {
            break;
          }
        }

        continue;
      }

      // Update status to uploading
      await updateChunkStatus(
        chunk.conversation_id,
        chunk.chunk_seq,
        'uploading',
        chunk.retry_count
      );

      // Check and refill URL queue if needed
      await checkAndRefillUrlQueue(currentConversationId, currentUserId);

      // Get next URL from queue
      const urlItem = getNextUrl();

      if (!urlItem) {
        logger.warn('No presigned URL available, retrying in 2 seconds', {
          component: 'audioS3Manager',
          action: 'NO_URL_AVAILABLE'
        });

        // Revert status back to pending
        await updateChunkStatus(
          chunk.conversation_id,
          chunk.chunk_seq,
          'pending',
          chunk.retry_count
        );

        // Try to fetch URLs immediately
        try {
          await fetchPresignedUrls(currentConversationId, currentUserId, URL_BATCH_SIZE);
        } catch (error) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        continue;
      }

      // Attempt upload
      try {
        await uploadChunkToS3(chunk, urlItem);

        // Upload successful - delete chunk from IndexedDB
        await deleteChunk(chunk.conversation_id, chunk.chunk_seq);

      } catch (uploadError) {
        // Upload failed - increment retry count
        const newRetryCount = chunk.retry_count + 1;

        if (newRetryCount >= MAX_RETRY_COUNT) {
          // Max retries reached - mark as failed
          await updateChunkStatus(
            chunk.conversation_id,
            chunk.chunk_seq,
            'failed',
            newRetryCount
          );

          logger.error('Chunk upload failed after max retries', {
            component: 'audioS3Manager',
            action: 'UPLOAD_MAX_RETRIES',
            context: {
              conversationId: chunk.conversation_id,
              chunkSeq: chunk.chunk_seq,
              retryCount: newRetryCount
            }
          });

          // Requeue failed chunk
          await updateChunkStatus(
            chunk.conversation_id,
            chunk.chunk_seq,
            'pending',
            0
          );
        } else {
          // Revert to pending for retry
          await updateChunkStatus(
            chunk.conversation_id,
            chunk.chunk_seq,
            'pending',
            newRetryCount
          );

          logger.warn('Chunk upload failed, will retry', {
            component: 'audioS3Manager',
            action: 'UPLOAD_RETRY',
            context: {
              conversationId: chunk.conversation_id,
              chunkSeq: chunk.chunk_seq,
              retryCount: newRetryCount
            }
          });

          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, UPLOAD_RETRY_DELAY_MS));
        }
      }

    } catch (error) {
      logger.error('Upload worker error', {
        component: 'audioS3Manager',
        action: 'UPLOAD_WORKER_ERROR',
        error
      });

      // Wait before continuing
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  logger.info('Upload worker stopped', {
    component: 'audioS3Manager',
    action: 'UPLOAD_WORKER_STOP',
    context: { conversationId: currentConversationId }
  });
}

// ==================== MAIN ORCHESTRATOR ====================

/**
 * Start audio recording and S3 upload process
 */
export async function startAudioS3Recording(conversationId, userId, stream) {
  try {
    logger.info('Starting audio S3 recording (WAV encoder)', {
      component: 'audioS3Manager',
      action: 'START_AUDIO_S3_RECORDING',
      context: { conversationId, userId, encoding: 'WAV' }
    });

    // Verify WAV encoder is ready
    if (!isWavEncoderReady()) {
      logger.warn('WAV encoder not initialized - ensure initializeWavEncoder() was called', {
        component: 'audioS3Manager',
        action: 'ENCODER_NOT_READY',
        context: { conversationId }
      });
    }

    const isNewConversation = conversationId !== currentConversationId;
    currentConversationId = conversationId;
    currentUserId = userId;
    if (isNewConversation) {
      chunkSequence = 0;
    }
    presignedQueue = [];

    // Initialize IndexedDB
    await initializeDatabase();

    // Fetch initial batch of presigned URLs
    await fetchPresignedUrls(conversationId, userId, URL_BATCH_SIZE);

    // Initialize MediaRecorder
    initializeMediaRecorder(stream, conversationId);

    // Start recording
    startRecording();

    // Start upload worker
    isUploadWorkerActive = true;
    uploadWorkerPromise = uploadWorkerLoop();

    logger.info('Audio S3 recording started successfully (WAV encoding)', {
      component: 'audioS3Manager',
      action: 'AUDIO_S3_RECORDING_STARTED',
      context: {
        conversationId,
        initialUrlCount: presignedQueue.length,
        encoding: 'WAV',
        chunk_safety: 'Each chunk independently playable'
      }
    });

    return {
      success: true,
      message: 'Audio S3 recording started',
      encoding: 'WAV'
    };

  } catch (error) {
    logger.error('Failed to start audio S3 recording', {
      component: 'audioS3Manager',
      action: 'START_AUDIO_S3_RECORDING_FAILED',
      error,
      context: { conversationId }
    });

    throw error;
  }
}

/**
 * Stop audio recording and wait for all uploads to complete
 * 
 * Since each chunk is a complete WAV file, the final chunk flush is reliable.
 */
export async function stopAudioS3Recording() {
  logger.info('Stopping audio S3 recording - WAITING for all uploads (WAV chunks)', {
    component: 'audioS3Manager',
    action: 'STOP_AUDIO_S3_RECORDING_WITH_UPLOADS',
    context: {
      conversationId: currentConversationId,
      encoding: 'WAV'
    }
  });

  // 1. Stop recording NEW chunks immediately
  stopRecording();  
  isUploadWorkerActive = false;  

  // 2. **WAIT FOR ZERO PENDING CHUNKS** - Blocks until ALL uploaded
  const maxWaitMs = 30000; // 30 seconds max
  const startWait = Date.now();
  
  while ((Date.now() - startWait) < maxWaitMs) {
    const pendingCount = await getPendingChunksCount(currentConversationId);
    
    if (pendingCount === 0) {
      logger.info('ALL uploads completed successfully (WAV chunks)', {
        component: 'audioS3Manager',
        action: 'ALL_UPLOADS_COMPLETE',
        context: { 
          conversationId: currentConversationId,
          totalChunks: chunkSequence,
          waitTimeMs: Date.now() - startWait,
          encoding: 'WAV',
          merge_ready: true
        }
      });
      break;
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    logger.debug(`Waiting for uploads... ${pendingCount} pending (WAV chunks)`, {
      component: 'audioS3Manager',
      action: 'UPLOADS_PENDING',
      context: { pendingCount, elapsedMs: Date.now() - startWait }
    });
  }

  // 3. Final check
  const finalPendingCount = await getPendingChunksCount(currentConversationId);
  if (finalPendingCount > 0) {
    logger.warn('Timeout waiting for uploads - some chunks remain pending', {
      component: 'audioS3Manager',
      action: 'UPLOAD_TIMEOUT',
      context: { finalPendingCount, conversationId: currentConversationId }
    });
  }

  // 4. Cleanup after all uploads complete
  cleanup();

  return {
    success: true,
    totalChunks: chunkSequence,
    pendingChunks: finalPendingCount,
    waitTimeMs: Date.now() - startWait,
    encoding: 'WAV'
  };
}


/**
 * Cleanup resources
 */
function cleanup() {
  // Clear chunk timer first
  if (chunkTimerRef) {
    clearInterval(chunkTimerRef);
    chunkTimerRef = null;
  }

  if (mediaRecorder) {
    mediaRecorder.ondataavailable = null;
    mediaRecorder.onerror = null;
    mediaRecorder = null;
  }

  presignedQueue = [];
  chunkSequence = 0;
  isRecordingActive = false;
  isUploadWorkerActive = false;
  currentConversationId = null;
  currentUserId = null;
  uploadWorkerPromise = null;
  isPausedState = false;

  logger.debug('Audio S3 manager cleaned up', {
    component: 'audioS3Manager',
    action: 'CLEANUP_COMPLETE'
  });
}

/**
 * Get current upload statistics (for monitoring/debugging)
 */

export function getRecordedAudioDurationS() {
  return (chunkSequence * CHUNK_DURATION_MS) / 1000;
}

export async function getUploadStats(conversationId) {
  if (!db) {
    return null;
  }

  try {
    const pendingCount = await getPendingChunksCount(conversationId);

    return {
      queueLength: presignedQueue.length,
      pendingChunks: pendingCount,
      currentChunkSeq: chunkSequence,
      isRecording: isRecordingActive,
      isUploading: isUploadWorkerActive
    };
  } catch (error) {
    logger.error('Failed to get upload stats', {
      component: 'audioS3Manager',
      action: 'GET_UPLOAD_STATS_FAILED',
      error
    });
    return null;
  }
}

/**
 * Pause audio recording (pauses MediaRecorder, upload worker continues)
 */
export function pauseAudioRecording() {
  isPausedState = true; 
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    mediaRecorder.pause();

    logger.info('Audio recording paused', {
      component: 'audioS3Manager',
      action: 'RECORDING_PAUSED',
      context: { conversationId: currentConversationId }
    });
  }
}

/**
 * Resume audio recording
 */
export function resumeAudioRecording() {
  isPausedState = false;
  if (mediaRecorder && mediaRecorder.state === 'paused') {
    mediaRecorder.resume();

    logger.info('Audio recording resumed', {
      component: 'audioS3Manager',
      action: 'RECORDING_RESUMED',
      context: { conversationId: currentConversationId }
    });
  }
}